import { useMemo } from "react";

function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Hook to match topics against input text in real-time.
 * Invariant Rule: Exactly at most 1 topic per context.
 * Longer topic names take precedence over shorter ones.
 */
export default function useTopicMatcher(inputText = "", allTopics = []) {
    return useMemo(() => {
        if (!inputText || !Array.isArray(allTopics) || allTopics.length === 0) {
            return {
                matchedTopics: [],
                signalsText: inputText || "",
                hasMatched: false,
            };
        }

        // 1. Strip existing signals to get clean plain text
        const plainText = inputText.replace(/\[\[(.*?)(?:\|(.*?))?\]\]/g, "$1");

        // 2. Filter topics that have valid context and name
        const validTopics = allTopics.filter(
            (t) => t && t.name && (t.context || "").trim() !== ""
        );

        // Sort descending by name length
        validTopics.sort((a, b) => (b.name || "").length - (a.name || "").length);

        const matchedTopics = [];
        const matchedContexts = new Set();
        const lowerPlainText = plainText.toLowerCase();

        for (const topic of validTopics) {
            const topicName = (topic.name || "").trim();
            const topicContext = (topic.context || "").trim();
            const lowerName = topicName.toLowerCase();

            if (!topicName || matchedContexts.has(topicContext.toLowerCase())) {
                continue;
            }

            let isMatched = false;
            try {
                const pattern = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapeRegExp(lowerName)}(?=[^\\p{L}\\p{N}]|$)`, "ui");
                isMatched = pattern.test(lowerPlainText);
            } catch (e) {
                isMatched = lowerPlainText.includes(lowerName);
            }

            if (isMatched) {
                matchedTopics.push(topic);
                matchedContexts.add(topicContext.toLowerCase());
            }
        }

        // 3. Generate signalsText with [[TopicName|ContextName]]
        let signalsText = plainText;
        for (const topic of matchedTopics) {
            const escaped = escapeRegExp(topic.name);
            // Replace if not already enclosed in [[ ... ]]
            const regex = new RegExp(`(?<!\\[\\[[^\\n\\]]*)(?:${escaped})(?![^\\[\\n]*\\]\\])`, "gi");
            signalsText = signalsText.replace(regex, `[[${topic.name}|${topic.context}]]`);
        }

        return {
            matchedTopics,
            signalsText,
            hasMatched: matchedTopics.length > 0,
        };
    }, [inputText, allTopics]);
}
